import { CSVRow, loadCSVFromFile } from "../../../lib/csvLoader";
import {
  Profiler,
  Question,
  QuestionId,
  QuestionSet,
  SingleChoiceQuestion,
  SliderQuestion,
} from "../types/Question";
import { answerRepository } from "./AnswerRepository";

export class QuestionRepository {
  private questions: Question[] = [];
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Ensure answer repository is initialized first
      await answerRepository.initialize();

      const questionsData = await loadCSVFromFile("/data/questions.csv");
      this.questions = questionsData
        .map((row: CSVRow) => {
          const answerOptionIds = row.answerOptions
            .split(",")
            .map((id: string) => parseInt(id.trim()));
          const options = answerRepository.getAnswersByIds(answerOptionIds);

          if (row.type === "single-choice") {
            return {
              id: parseInt(row.id) as QuestionId,
              number: parseInt(row.number) as number,
              question: row.question || "",
              backgroundPath: row.backgroundPath || "",
              options,
              type: "single-choice",
              profiler: row.profiler as Profiler,
              variant: row.variant as "black" | "white",
            } as SingleChoiceQuestion;
          }
          if (row.type === "slider") {
            return {
              id: parseInt(row.id) as QuestionId,
              number: parseInt(row.number) as number,
              question: row.question || "",
              backgroundPath: row.backgroundPath || "",
              minLabel: row.minLabel || "",
              maxLabel: row.maxLabel || "",
              options,
              type: "slider",
              profiler: row.profiler as Profiler,
              variant: row.variant as "black" | "white",
            } as SliderQuestion;
          }
          return undefined;
        })
        .filter((question): question is Question => question !== undefined);

      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize QuestionRepository:", error);
      throw error;
    }
  }

  getQuestionSetByProfiler(profiler: Profiler): QuestionSet {
    if (!this.isInitialized) {
      throw new Error(
        "QuestionRepository not initialized. Call initialize() first."
      );
    }
    const questionSet = this.questions
      .filter((question) => question.profiler === profiler)
      .map((question) => question.id);
    return questionSet;
  }

  getQuestionById(id: QuestionId): Question | undefined {
    return this.questions.find((question) => question.id === id);
  }

  getQuestionsCountByProfiler(profiler: Profiler): number {
    if (!this.isInitialized) {
      throw new Error(
        "QuestionRepository not initialized. Call initialize() first."
      );
    }
    return this.getQuestionSetByProfiler(profiler).length;
  }

  isReady(): boolean {
    return this.isInitialized;
  }
}

export const questionRepository = new QuestionRepository();
